import { useState, useEffect, useCallback } from 'react';
import { practiceDataUtil } from '../helpers/practiceDataUtil';
import { useToast } from './use-toast';

export function usePracticeSync({ autoInitialize = true }) {
  const [loading, setLoading] = useState(autoInitialize);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [hasUpdates, setHasUpdates] = useState(false);
  const { toast } = useToast();
  
  // Initialize on mount if autoInitialize is true
  useEffect(() => {
    if (!autoInitialize) return;
    
    let isMounted = true;
    
    const initialize = async () => {
      try {
        await practiceDataUtil.initializePracticeData();
        
        if (isMounted) {
          setInitialized(true);
          setLoading(false);
          
          // Check for updates - make sure this function exists in practiceDataUtil
          try {
            const updateStatus = await practiceDataUtil.checkForUpdatesAvailable();
            setHasUpdates(updateStatus?.available || false);          } catch (updateError) {
            if (process.env.NODE_ENV === 'development') {
              console.error('[PRACTICE-ERROR] Error checking for updates:', updateError);
            }
          }
        }      } catch (error) {
        if (isMounted) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[PRACTICE-ERROR] usePracticeSync:', error);
          }
          setError(error.message);
          setLoading(false);
        }
      }
    };
    
    initialize();
    
    return () => {
      isMounted = false;
    };
  }, [autoInitialize]);
  
  // Get practice by code
  const getPractice = useCallback(async (code) => {
    if (!code) return null;
      try {
      return await practiceDataUtil.getPracticeByCode(code);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting practice:', error);
      }
      return null;
    }
  }, []);
  
  // Add the missing getAndCachePractice function - this was referenced but not defined
  const getAndCachePractice = useCallback(async (code, forceRefresh = false) => {
    if (!code) return null;
    
    try {
      //console.log(`Getting practice with code: ${code}, forceRefresh: ${forceRefresh}`);
      const practice = await practiceDataUtil.getPracticeByCode(code, forceRefresh);
      
      if (practice) {
        // Cache the practice in localStorage for faster access next time
        try {
          const cacheKey = `practice_${code}`;
          localStorage.setItem(cacheKey, JSON.stringify({
            data: practice,
            timestamp: Date.now()
          }));        } catch (cacheError) {
          // Ignore cache errors, they're non-critical
          if (process.env.NODE_ENV === 'development') {
            console.warn('Could not cache practice data:', cacheError);
          }
        }
      }
        return practice;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Error in getAndCachePractice for code ${code}:`, error);
      }
      return null;
    }
  }, []);
  
  // Check for updates with error handling
  const checkForUpdates = useCallback(async (forceRefresh = false) => {
    try {
      // Use optional chaining and provide default value to make this more robust
      const result = await practiceDataUtil.checkForUpdatesAvailable(forceRefresh);
      setHasUpdates(result?.available || false);      return result || { available: false };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error checking for updates:', error);
      }
      return { available: false, error };
    }
  }, []);
  
  // Force refresh
  const forceRefresh = useCallback(async () => {
    setLoading(true);
    try {
      await practiceDataUtil.forceRefresh();
      setHasUpdates(false);
      toast({
        title: "Data Refreshed",
        description: "Practice data has been updated with latest information",
        variant: "success"      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error forcing refresh:', error);
      }
      toast({
        title: "Refresh Failed",
        description: "Could not refresh practice data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);
  
  return {
    loading,
    initialized,
    error,
    hasUpdates,
    getPractice,
    getAndCachePractice, // Add this export
    checkForUpdates,
    forceRefresh
  };
}
